import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Typography,
  Grid,
  Box,
  CircularProgress,
  Paper,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { usersAPI } from '../../../services/api';
import ToggleButtonGroup from '../../UIKit/ToggleButtonGroup';
import SearchInput from '../../UIKit/SearchInput';
import { Info, ViewList, ViewModule } from '@mui/icons-material';
import UserTable from './UserTable';
import UserCards from './UserCards';
import UserModals from './UserModals';
import UserPagination from './UserPagination';

const UserManagementPanel = () => {
  const { t } = useTranslation(['admin', 'common']);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const { isAuthenticated, user: currentUser } = useAuth();
  const [viewMode, setViewMode] = useState(() => {
    return window.innerWidth < 768 ? 'card' : 'table';
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Add resize listener to update view mode
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && viewMode === 'table') {
        setViewMode('card');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  // Мемоизированные параметры выборки
  const fetchParams = useMemo(() => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
    };
    if (sortBy && sortOrder) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }
    if (search.trim()) {
      params.search = search.trim();
    }
    return params;
  }, [pagination.page, pagination.limit, sortBy, sortOrder, search]);

  // Защита от гонок
  const requestIdRef = useRef(0);

  const fetchUsers = useCallback(async () => {
    const currentId = ++requestIdRef.current;
    try {
      setLoading(true);
      const response = await usersAPI.getAll(fetchParams);
      if (currentId !== requestIdRef.current) return;
      setUsers(response.data.data.users);
      setPagination(response.data.data.pagination);
    } catch (err) {
      if (currentId !== requestIdRef.current) return;
      console.error('Ошибка при загрузке пользователей:', err);
      toast.error(
        t('admin:users.messages.error.load', {
          message: err.response?.data?.message || err.message,
        })
      );
    } finally {
      if (currentId === requestIdRef.current) setLoading(false);
    }
  }, [fetchParams, t]);

  // Загрузка пользователей (без зависимости от viewMode)
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    }
  }, [isAuthenticated, fetchUsers]);


  // Обработчики модальных окон
  const handleCloseModals = useCallback(() => {
    setShowBanModal(false);
    setSelectedUser(null);
    setBanReason('');
  }, []);

  // Обработка изменения формы
  const handleBanReasonChange = useCallback((e) => {
    setBanReason(e.target.value);
  }, []);

  // Обработчик сортировки
  const handleSort = useCallback((field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [sortBy, sortOrder]);

  // Получение иконки сортировки
  const getSortIcon = useCallback((field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  }, [sortBy, sortOrder]);

  // Обработчик поиска
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleToggleBan = async () => {
    if (!selectedUser) return;
    
    // Определяем текущий статус активности пользователя
    const isCurrentlyActive = selectedUser.isActive;
    
    // Если блокируем (пользователь активен), то нужна причина
    if (isCurrentlyActive && !banReason.trim()) {
      toast.error(t('admin:users.messages.error.ban_reason_required'));
      return;
    }

    setUserActionLoading(true);
    try {
      if (isCurrentlyActive) {
        await usersAPI.ban(selectedUser.id, banReason.trim());
      } else {
        await usersAPI.unban(selectedUser.id);
      }

      // Перезапрашиваем список пользователей для консистентности
      await fetchUsers();

      handleCloseModals();
      toast.success(
        t(
          isCurrentlyActive
            ? 'admin:users.messages.success.deactivate'
            : 'admin:users.messages.success.activate'
        )
      );
    } catch (err) {
      toast.error(
        t('admin:users.messages.error.activity', {
          message: err.response?.data?.message || err.message,
        })
      );
    } finally {
      setUserActionLoading(false);
    }
  };

  const handleToggleActivity = useCallback(async (userId, currentIsActive) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Показываем модальное окно для блокировки/разблокировки
    setSelectedUser(user);
    setBanReason('');
    setShowBanModal(true);
  }, [users]);

  // Add view mode handler
  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  }, []);

  // Обработчик изменения страницы пагинации
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Обработчик изменения лимита пагинации
  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      page: 1,
      limit: Number(e.target.value),
    }));
  };

  return (
    <>
      <Grid
        container
        spacing={{ xs: 1, lg: 2 }}
        sx={{
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Grid item xs={12} lg>
          <Typography variant="h5" component="h1">
            {t('admin:users.title')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          lg={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            justifyContent: { xs: 'space-between', md: 'flex-end' },
            gap: 2,
            width: '100%',
          }}
        >
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder={t('common:search')}
            sx={{ minWidth: 200 }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
          >
            <ToggleButton value="table" aria-label={t('common:view.table')}>
              <Tooltip title={t('common:view.table')}>
                <ViewList />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="card" aria-label={t('common:view.card')}>
              <Tooltip title={t('common:view.card')}>
                <ViewModule />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <Info color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">
            {t('admin:users.no_users')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('admin:users.no_users_description')}
          </Typography>
        </Paper>
      ) : viewMode === 'table' ? (
        <UserTable
          users={users}
          currentUser={currentUser}
          onToggleActivity={handleToggleActivity}
          getSortIcon={getSortIcon}
          onSort={handleSort}
        />
      ) : (
        <UserCards
          users={users}
          currentUser={currentUser}
          onToggleActivity={handleToggleActivity}
        />
      )}

      {/* Компонент пагинации */}
      {!loading && (
        <UserPagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
        />
      )}

      {/* Модальные окна */}
      <UserModals
        showBanModal={showBanModal}
        selectedUser={selectedUser}
        banReason={banReason}
        userActionLoading={userActionLoading}
        onClose={handleCloseModals}
        onToggleBan={handleToggleBan}
        onBanReasonChange={handleBanReasonChange}
      />
    </>
  );
};

export default UserManagementPanel; 